// ---------------------------------------------------------------------------
// Variable bindings — shared, React-free helpers for wiring an evaluation's
// declared variables to their runtime `$expr` sources. Used by every eval
// surface (eval template form, agent Evaluate tab, grader Evaluate tab) so the
// binding UI and the emitted `with:` clauses stay consistent.
//
// A binding maps a variable name to an expression the platform resolves per
// trial, e.g. `{{$expr:$.queryItem.data.userQuery}}` (a query-row field) or
// `{{$expr:$.turn.output}}` (the subject-under-test's output).
// ---------------------------------------------------------------------------

import type { TemplateBodyParts } from './EvaluationTemplateForm.utils.js';

/** A per-variable runtime binding (variable name → `$expr` expression). */
export interface BindingRow {
  name: string;
  expr: string;
  defaultValue?: unknown;
  declaredBy?: string[];
  type?: string;
  templateVariableId?: string;
}

// UPPER_SNAKE_CASE → camelCase (USER_QUERY → userQuery). Used to guess a
// query-row field name for a variable's default binding.
export function toCamelCase(name: string): string {
  return name
    .toLowerCase()
    .replace(/_([a-z0-9])/g, (_m, c: string) => c.toUpperCase());
}

/**
 * Convention default for a variable's runtime binding: pull the value from the
 * matching query-row field. Only a starting guess — every binding is editable
 * in the Bindings card. Callers with extra context (e.g. the grader Evaluate
 * tab, whose subject output lives at `$.turn.output`) wrap this with their own
 * special cases.
 */
export function defaultBindingFor(varName: string): string {
  return `{{$expr:$.queryItem.data.${toCamelCase(varName)}}}`;
}

// Minimal wire shape of a built task. `buildTemplateBody` emits tasks with only
// `name`/`agent`/`graders`; we attach `with:` bindings to the agent + grader
// refs here.
interface TaskWire {
  agent?: { name: string; with?: Record<string, string> };
  graders?: { name: string; with?: Record<string, string> }[];
  [key: string]: unknown;
}

/**
 * Attach `with:` bindings to every task's agent + grader refs. Row-derived
 * bindings (anything not reading `$.turn`) go on the agent — where they feed
 * the subject under test — and on every grader; turn-output bindings go on
 * graders only, since the turn doesn't exist during the agent phase. Blank
 * expressions are treated as "no binding" and skipped.
 */
export function applyBindingsToParts(
  parts: TemplateBodyParts,
  bindingRows: BindingRow[],
): TemplateBodyParts {
  const agentWith: Record<string, string> = {};
  const graderWith: Record<string, string> = {};
  for (const { name, expr } of bindingRows) {
    const trimmed = expr.trim();
    if (trimmed.length === 0) continue;
    graderWith[name] = trimmed;
    if (!trimmed.includes('$.turn')) agentWith[name] = trimmed;
  }

  if (
    Object.keys(agentWith).length === 0 &&
    Object.keys(graderWith).length === 0
  ) {
    return parts;
  }

  const tasks = (parts.tasks as unknown as TaskWire[]).map((t) => ({
    ...t,
    ...(t.agent && Object.keys(agentWith).length > 0
      ? { agent: { ...t.agent, with: agentWith } }
      : {}),
    ...(t.graders && Object.keys(graderWith).length > 0
      ? { graders: t.graders.map((g) => ({ ...g, with: graderWith })) }
      : {}),
  }));

  return { ...parts, tasks };
}

// A saved task, as read back from a template's GET response — only the parts we
// round-trip bindings from.
interface SeededTask {
  agent?: { with?: Record<string, string> };
  graders?: { with?: Record<string, string> }[];
}

/**
 * Recover the variable→expression overrides a saved template stored in its
 * `with:` clauses, so editing a template re-populates the Bindings card. Merges
 * every ref's `with:` (graders carry the superset — turn + row bindings — while
 * the agent carries only row bindings).
 */
export function extractBindings(task: unknown): Record<string, string> {
  const t = (task ?? {}) as SeededTask;
  const merged: Record<string, string> = {};
  for (const [name, expr] of Object.entries(t.agent?.with ?? {})) {
    merged[name] = expr;
  }
  for (const g of t.graders ?? []) {
    for (const [name, expr] of Object.entries(g.with ?? {})) {
      merged[name] = expr;
    }
  }
  return merged;
}
